import { snapdom } from '@zumer/snapdom';

// A stalled load/rasterize must never block the pipeline, so each async step races this timeout.
const STEP_TIMEOUT_MS = 8000;

async function withTimeout<T>(
    promise: Promise<T>,
    label: string,
    ms = STEP_TIMEOUT_MS,
): Promise<T> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_resolve, reject) => {
        timer = setTimeout(
            () => reject(new Error(`HtmlCompositionRenderer: ${label} timed out after ${ms}ms`)),
            ms,
        );
    });
    try {
        return await Promise.race([promise, timeout]);
    } finally {
        clearTimeout(timer);
    }
}

// The composition loads into a same-origin srcdoc iframe that inherits the host CSP; under a strict
// nonce-based CSP its inline <script>/<style> would be blocked, so stamp the host nonce
// (window._CSP_NONCE) onto them. No-op when there's no nonce (NW.js render worker, tests).
function applyCspNonce(html: string): string {
    const nonce =
        typeof window !== 'undefined'
            ? (window as unknown as { _CSP_NONCE?: string })._CSP_NONCE
            : undefined;
    if (!nonce) {
        return html;
    }
    return html
        .replace(/<script\b(?![^>]*\bnonce=)/gi, `<script nonce="${nonce}"`)
        .replace(/<style\b(?![^>]*\bnonce=)/gi, `<style nonce="${nonce}"`);
}

/**
 * The seekable timeline a composition exposes at `window.__timelines.main`, driven by normalized
 * progress (0..1). `suppressEvents = false` so onUpdate-driven content (counters, tweens) fires
 * while seeking.
 */
export interface HtmlSeekableTimeline {
    progress(value: number, suppressEvents?: boolean): unknown;
}

/**
 * Loads an HTML composition into a hidden off-screen iframe, seeks its timeline, and rasterizes the
 * result with snapDOM — the deterministic, progress-driven DOM→canvas path HtmlAsset composites
 * into the scene.
 */
export class HtmlCompositionRenderer {
    #iframe: HTMLIFrameElement | null = null;
    #readyPromise: Promise<void> | null = null;
    #loadedHtml: string | null = null;
    #ready = false;
    #width: number;
    #height: number;
    #destroyed = false;

    constructor(width: number, height: number) {
        this.#width = width;
        this.#height = height;
    }

    get isReady(): boolean {
        return this.#ready;
    }

    /** The loaded composition document, for runtime introspection (lint, NTE). Null until loaded. */
    get contentDocument(): Document | null {
        return this.#iframe?.contentDocument ?? null;
    }

    /**
     * The raw `window.__timelines.main` timeline (a gsap timeline in practice), for runtime
     * introspection beyond the seek-only `HtmlSeekableTimeline`. Null when none is declared.
     */
    getTimelineObject(): unknown {
        const win = this.#iframe?.contentWindow as
            | (Window & { __timelines?: { main?: unknown } })
            | null
            | undefined;
        return win?.__timelines?.main ?? null;
    }

    setSize(width: number, height: number): void {
        if (this.#width === width && this.#height === height) {
            return;
        }
        this.#width = width;
        this.#height = height;
        if (this.#iframe) {
            this.#iframe.style.width = `${width}px`;
            this.#iframe.style.height = `${height}px`;
            this.#iframe.width = String(width);
            this.#iframe.height = String(height);
        }
    }

    /** Load (or reload) the composition into the iframe; resolves once its scripts have run. Idempotent. */
    async load(html: string): Promise<void> {
        if (this.#loadedHtml === html && this.#readyPromise !== null) {
            return this.#readyPromise;
        }
        this.#loadedHtml = html;
        this.#ready = false;
        this.#ensureIframe();

        const iframe = this.#iframe;
        if (!iframe) {
            this.#readyPromise = Promise.resolve();
            return this.#readyPromise;
        }

        const loadPromise = new Promise<void>((resolve) => {
            const onLoad = () => {
                this.#ready = true;
                resolve();
            };
            iframe.addEventListener('load', onLoad, { once: true });
            iframe.srcdoc = applyCspNonce(html);
        });
        this.#readyPromise = withTimeout(loadPromise, 'iframe load');
        return this.#readyPromise;
    }

    /** Seek the composition's timeline to normalized progress. No-op when it declares no timeline. */
    seek(progress: number): void {
        const timeline = this.#timeline();
        if (!timeline) {
            return;
        }
        const clamped = Math.max(0, Math.min(1, progress));
        try {
            timeline.progress(clamped, false);
        } catch {
            // A malformed timeline shouldn't crash the render; the lint layer surfaces it.
        }
    }

    /**
     * Rasterize the iframe document to a canvas via snapDOM. `scale` multiplies the rasterized pixel
     * density relative to the layout size, matching the output resolution it's composited into.
     *
     * Fonts are NOT passed via snapDOM's `embedFonts`/`localFonts`: the template fonts are injected
     * into the composition as data-URI `@font-face` rules (see `htmlManagement`), and snapDOM keeps
     * that `<style>` when it clones the DOM into the SVG, so the fonts embed for free. Passing them
     * again only added a redundant font-discovery pass and a second copy of the bytes per frame.
     */
    async rasterize(scale: number): Promise<HTMLCanvasElement> {
        const doc = this.#iframe?.contentDocument;
        if (!doc || !doc.documentElement) {
            throw new Error('HtmlCompositionRenderer: iframe document is not available');
        }
        return withTimeout(
            snapdom.toCanvas(doc.documentElement, {
                backgroundColor: 'transparent',
                width: this.#width,
                height: this.#height,
                dpr: scale,
            }),
            'snapdom rasterize',
        );
    }

    destroy(): void {
        this.#destroyed = true;
        this.#ready = false;
        this.#readyPromise = null;
        this.#loadedHtml = null;
        if (this.#iframe && this.#iframe.parentNode) {
            this.#iframe.parentNode.removeChild(this.#iframe);
        }
        this.#iframe = null;
    }

    #timeline(): HtmlSeekableTimeline | null {
        const win = this.#iframe?.contentWindow as
            | (Window & { __timelines?: { main?: HtmlSeekableTimeline } })
            | null
            | undefined;
        return win?.__timelines?.main ?? null;
    }

    #ensureIframe(): void {
        if (this.#iframe || this.#destroyed || typeof document === 'undefined') {
            return;
        }
        const iframe = document.createElement('iframe');
        iframe.setAttribute('aria-hidden', 'true');
        iframe.setAttribute('tabindex', '-1');
        // Off-screen but still laid out and rendered: `display:none` would zero the document's
        // dimensions and break layout/rasterization, so we position it out of view instead.
        iframe.style.position = 'fixed';
        iframe.style.top = '0';
        iframe.style.left = '-99999px';
        iframe.style.border = '0';
        iframe.style.pointerEvents = 'none';
        iframe.style.width = `${this.#width}px`;
        iframe.style.height = `${this.#height}px`;
        iframe.width = String(this.#width);
        iframe.height = String(this.#height);
        document.body.appendChild(iframe);
        this.#iframe = iframe;
    }
}
